// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type Task,
  type TaskAssignee,
  type TaskAssigneeTaskInProgress,
  useEvolveGet,
  type UserId,
} from '@evolvemep/api-ts';
import { Button, Card, Divider, Flex, Stack, Text, Title } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { RunningTime } from 'components/Mantine/RunningTime';
import { WrappedSwitch } from 'components/Mantine/WrappedSwitch';
import { isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { AssigneesAlreadyActiveModal } from 'modules/Shop/Tasks/ManageTaskTimeModal/AssigneesAlreadyActiveModal';
import { EditTaskAssigneesPopover } from 'modules/Shop/Tasks/TaskViewer/EditTaskAssigneesPopover';

const TimerModalAssignee = ({
  task,
  isActive,
  assignee,
  onToggleAssignee,
  loading,
  isRunning,
}: {
  task: Task;
  assignee: TaskAssignee;
  isActive: boolean;
  onToggleAssignee: () => void;
  loading?: boolean;
  isRunning?: boolean;
}) => {
  const { apiCall: getTasksInProgress, loading: loadingInProgressTasks } = useEvolveGet(
    `shop/task/assignee/${assignee.user.userId}/inProgressTasks`,
    { defaultConfig: { params: { otherThan: task.taskId } } },
  );
  const [wasUpdated, setWasUpdated] = useState(false);
  const [blockedAssignees, setBlockedAssignees] = useState<TaskAssigneeTaskInProgress[] | null>(null);
  const handleToggle = async () => {
    if (isRunning && !isActive) {
      // Prompt the user if this assignee is already on a running task
      const retrievedBlockedAssignees = await getTasksInProgress();
      if (isNotNil(retrievedBlockedAssignees)) {
        setBlockedAssignees([retrievedBlockedAssignees]);
        return;
      }
    }
    onToggleAssignee();
    if (isRunning && isActive) {
      setWasUpdated(true);
      setTimeout(() => setWasUpdated(false), 2000);
    }
  };
  return (
    <>
      <Flex justify="space-between" align="center" gap="md">
        <Text truncate>{validateUserNameOrEmail(assignee.user)}</Text>
        <Flex wrap="nowrap" align="center" gap="xs">
          <Text
            c="green.5"
            style={{
              display: 'block',
              textWrap: 'nowrap',
              transition: 'all 0.2s ease-in-out',
              opacity: wasUpdated ? 1 : 0,
              width: wasUpdated ? 'auto' : 0,
              overflow: 'hidden',
            }}
            hidden={!wasUpdated}
          >
            Time entry saved!
          </Text>
          <Text c="dimmed" style={{ textWrap: 'nowrap' }}>
            <RunningTime timeInSeconds={assignee.totalSeconds} isRunning={isActive && isRunning} showSeconds />
          </Text>
          <WrappedSwitch onClick={handleToggle} checked={isActive} loading={loading || loadingInProgressTasks} />
        </Flex>
      </Flex>
      <Divider />
      <AssigneesAlreadyActiveModal
        blockedAssignees={blockedAssignees}
        onClose={() => setBlockedAssignees(null)}
        onConfirm={onToggleAssignee}
      />
    </>
  );
};
export const TimerModalAssignees = ({
  task,
  activeUserIds,
  onToggleAssignee,
  onTaskUpdated,
  loading,
}: {
  task: Task;
  activeUserIds: UserId[];
  onToggleAssignee: (u: UserId) => void;
  onTaskUpdated: (t: Task) => void;
  loading: boolean;
}) => {
  const [showAddAssigneesPopover, setShowAddAssigneesPopover] = useState(false);

  return (
    <Card bg="gray.0" withBorder m="md" px="lg" pos="relative">
      <Stack>
        {task.assignees.map((assignee) => (
          <TimerModalAssignee
            key={assignee.user.userId}
            task={task}
            isActive={activeUserIds.includes(assignee.user.userId)}
            assignee={assignee}
            onToggleAssignee={() => onToggleAssignee(assignee.user.userId)}
            loading={loading}
            isRunning={task.taskStatusTypeName === 'Started'}
          />
        ))}
        {task.assignees.length === 0 && (
          <Stack align="center" gap="xs">
            <Title order={4}>Assignees</Title>
            <Text size="sm">Add assignees to this task to track individual labor times.</Text>
          </Stack>
        )}

        <Button
          variant="light"
          leftSection={<EvolveIcon icon="Add" />}
          onClick={() => setShowAddAssigneesPopover(true)}
        >
          Add assignees
        </Button>
        <EditTaskAssigneesPopover
          task={task}
          onTaskUpdated={onTaskUpdated}
          opened={showAddAssigneesPopover}
          onClose={() => setShowAddAssigneesPopover(false)}
        />
      </Stack>
    </Card>
  );
};
